import {
  GetCtaSetResponse,
  GetLandingPagesResponse,
  GetWebsiteOverlaysResponse,
  LandingPageOption,
  LandingPageType,
  Option,
  PROMPT_TYPE,
} from './types';
import _ from 'lodash';
export const removeHTMLTags = (str: any) => {
  if (!str) return '';
  return str.toString().replace(/(<([^>]+)>)/gi, '');
};

export const generateSimplifiedCrmCode = (
  thumbnail: string,
  url: string,
  from: string
) => {
  if (!url) {
    return '';
  }
  const senderHtml = from ? `from ${from} ` : '';
  let thumbnailHtml = thumbnail
    ? `<div><a href="${url}">${thumbnail}</a></div><br />`
    : '';
  // remove the alt attribute from thumbnailHtml
  thumbnailHtml = thumbnailHtml.replace(/\salt=['"][^'"]*['"]/g, '');
  return `${thumbnailHtml}Click the image or view your video ${senderHtml}<a href="${url}">here</a>`;
};

export const prepareWebsiteOverlayOptions = (
  overlays: GetWebsiteOverlaysResponse
): { autoSelectedOption?: Option; options: Option[] } => {
  const websiteUrls = overlays.websiteUrls;
  let options: Option[] = [];
  let autoSelectedOption: Option | undefined;

  if (websiteUrls && websiteUrls.length > 0) {
    options = websiteUrls.map((e) => ({
      label: e.title,
      value: e.id.toString(),
    }));
    options = _.uniqBy(options, 'value');

    const findDefaultLink = websiteUrls.find((item) => item.defaultflag === 1);
    if (findDefaultLink && findDefaultLink.id) {
      autoSelectedOption = {
        value: findDefaultLink.id.toString(),
        label: findDefaultLink.title,
      };
    }
  }
  return { options, autoSelectedOption };
};

export const builderLandingPageTypeId = 19;

export const defaultLPOption: LandingPageOption = {
  value: 0,
  label: '',
  landingPageType: LandingPageType.VIDMAILS,
};

export const getSalesServiceOption = (isAutomotiveServiceRole: boolean) => {
  return {
    //has to be -2 so php doesn't add default if no LP selected
    value: -2,
    label: `${isAutomotiveServiceRole ? 'Service' : 'Sales'} page`,
    landingPageType: LandingPageType.AUTOMOTIVE,
  };
};

export const prepareTemplateLandingPageOptions = ({
  userData,
  templatesResponse,
  sendVehicle,
}: {
  userData: {
    isAutomotive: boolean;
    isAutomotiveSalesRole: boolean;
    isAutomotiveServiceRole: boolean;
  };
  templatesResponse: GetLandingPagesResponse;
  sendVehicle: boolean;
}): { options: LandingPageOption[]; autoSelectedOption: LandingPageOption } => {
  const { isAutomotive, isAutomotiveSalesRole, isAutomotiveServiceRole } =
    userData;
  // flag to disable cds landing page, to use vidmails only
  const disableCDSLandingPage = !isAutomotive;
  const salesServicePageOption = getSalesServiceOption(
    userData.isAutomotiveServiceRole
  );
  // flag to show only custom landing pages (built by our builder)
  const disableStaticLandingPages =
    isAutomotiveServiceRole || (isAutomotiveSalesRole && sendVehicle);

  let autoSelectedOption = disableCDSLandingPage
    ? defaultLPOption
    : salesServicePageOption;
  const defaultLandingPage = templatesResponse && templatesResponse.default;
  // filter our static lp options from response if static pages disabled, create options
  let options =
    templatesResponse &&
    templatesResponse.templates
      .filter(
        (template: any) =>
          !disableStaticLandingPages ||
          template.typeId === builderLandingPageTypeId
      )
      .map((template: any) => ({
        label: template.title,
        value: template.id,
        landingPageType:
          template.typeId !== builderLandingPageTypeId
            ? LandingPageType.VIDMAILS
            : LandingPageType.AUTOMOTIVE,
      }));
  // add automotive LP as option
  if (!disableCDSLandingPage) {
    options = [...options, salesServicePageOption];
  }
  // add default LP as option if static not disabled or default not static
  if (
    defaultLandingPage &&
    defaultLandingPage.id &&
    (!disableStaticLandingPages ||
      (defaultLandingPage &&
        defaultLandingPage.typeId === builderLandingPageTypeId))
  ) {
    const defaultLandingPageOption = {
      label: templatesResponse.default.title,
      value: templatesResponse.default.id,
      landingPageType:
        defaultLandingPage &&
        defaultLandingPage.typeId === builderLandingPageTypeId
          ? LandingPageType.AUTOMOTIVE
          : LandingPageType.VIDMAILS,
    };
    autoSelectedOption = defaultLandingPageOption;
    options = [...options, defaultLandingPageOption];
  }
  options = _.uniqBy(options, 'value');

  return { options, autoSelectedOption };
};

export const checkIfFeatureIsEnabled = (
  userData: any,
  featureId: string | number
): boolean => {
  if (!userData.authCalled || !featureId) {
    return false;
  }

  if (userData && userData.user && userData.user.packageDetails) {
    // legacy user has access to all features
    if (userData.user.packageDetails.id === 0) {
      return true;
    }

    if (
      !userData.user.packageDetails.products ||
      !userData.user.packageDetails.products.length
    ) {
      return false;
    }

    return userData.user.packageDetails.products.includes(featureId);
  }

  return false;
};

export const prepareLinksetCTAOptions = ({
  linksetsResponse,
}: {
  linksetsResponse: GetCtaSetResponse;
}): { autoSelectedOption?: Option; options: Option[] } => {
  const options: Option[] =
    linksetsResponse &&
    linksetsResponse.linksets.map((e) => ({
      label: e.title,
      value: e.id.toString(),
    }));
  const uniqueOptions = _.uniqBy(options, 'value');

  const findDefaultLink =
    linksetsResponse &&
    linksetsResponse.linksets.find((item) => item.defaultLinks === 1);
  let autoSelectedOption: Option | undefined;

  if (findDefaultLink) {
    autoSelectedOption = {
      value: findDefaultLink.id.toString(),
      label: findDefaultLink.title,
    };
  }

  return { options: uniqueOptions, autoSelectedOption };
};

export const checkIfOverlayValid = (
  templateId: string | number,
  overlayId: string | number
) => {
  // overlay is not valid if template is Website Overlay and overlay id is empty
  return (templateId && templateId.toString() !== '-1') || !!overlayId;
};

export const formatGeneratedText = (
  generatedText: string,
  promptType: PROMPT_TYPE
) => {
  if (promptType === PROMPT_TYPE.TELEPROPTER_SCRIPT) {
    // Remove any text that is in square brackets for teleprompter scripts.
    generatedText = generatedText.replace(/\[.*?\]/g, '');
    // Add new line after each punctuation for teleprompter scripts.
    generatedText = generatedText.replace(/([.!?])\s/g, '$1\n');
  }

  // Remove leading and trailing newlines
  generatedText = generatedText.replace(/^\n+|\n+$/g, '');

  // Change triple new lines to double new lines.
  generatedText = generatedText.replace(/\n{3,}/g, '\n\n');

  // Remove leading and trailing quotes
  generatedText = generatedText.replace(/^"/, '').replace(/"$/, '');

  // Remove double quotes ""
  generatedText = generatedText.replace(/""/g, '');

  return generatedText;
};
